import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, ArrowUpRight, Loader2, Link2 } from "lucide-react";
import { BRAND, PRODUCT_COPY, FIGHT_STUDIO_MEDIA } from "@/lib/brand";
import { Reveal, SectionLabel } from "@/components/Motion";
import { GiGallery } from "@/components/GiMedia";
import { useStore } from "@/context/StoreContext";
import { useCheckout } from "@/hooks/useCheckout";

const BuyButton = ({ product, big }) => {
  const { format } = useStore();
  const { buy, pending } = useCheckout();
  const busy = pending === product.slug;

  return (
    <button
      onClick={() => buy(product.slug)}
      data-testid={`buy-${product.slug}`}
      className={`group relative w-full overflow-hidden bg-white font-mono uppercase text-black ${
        big ? "py-4 text-[12px] tracking-[0.22em]" : "py-3.5 text-[11px] tracking-[0.18em]"
      }`}
    >
      <span className="relative z-10 flex items-center justify-center gap-2.5">
        {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
        {busy
          ? "Opening checkout…"
          : product.available
          ? `Buy — ${format(product.price)}`
          : "Notify me on Discord"}
      </span>
      <span className="absolute inset-0 z-0 -translate-y-full bg-[hsl(var(--orv-blue))] transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:translate-y-0" />
    </button>
  );
};

const PriceTag = ({ product, big }) => {
  const { format, code, isBase } = useStore();
  return (
    <div className={big ? "text-right" : ""}>
      <div
        data-testid={`price-${product.slug}`}
        className={`font-display font-extrabold tracking-tight text-white ${
          big ? "text-5xl sm:text-6xl" : "text-4xl"
        }`}
      >
        {format(product.price)}
      </div>
      <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.18em] text-white/35">
        {code} · one-time
      </div>
      {!isBase && (
        <div className="mt-1 font-mono text-[10px] tracking-[0.1em] text-white/25">
          ≈ ${product.price.toFixed(2)} USD billed
        </div>
      )}
    </div>
  );
};

const PairsWith = ({ text }) => (
  <div className="flex items-start gap-3 border-t border-white/8 px-8 py-5 sm:px-10">
    <Link2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-white/30" />
    <p className="text-[12px] leading-relaxed text-white/35">{text}</p>
  </div>
);

const Gallery = ({ badge }) => {
  const [active, setActive] = useState(0);
  const shot = FIGHT_STUDIO_MEDIA[active];

  return (
    <div className="border-b border-white/10">
      <div
        className={`relative overflow-hidden bg-[#08080a] ${
          shot.fit === "contain" ? "aspect-[5/4]" : "aspect-[16/9]"
        }`}
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={shot.src}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="absolute inset-0"
          >
            {shot.fit === "contain" && (
              <img
                src={shot.src}
                alt=""
                aria-hidden="true"
                className="absolute inset-0 h-full w-full scale-110 object-cover opacity-25 blur-2xl"
              />
            )}
            <img
              src={shot.src}
              alt={shot.caption}
              data-testid="product-image-fight-studio"
              className={`absolute inset-0 h-full w-full ${
                shot.fit === "contain" ? "object-contain py-2" : "object-cover"
              }`}
            />
          </motion.div>
        </AnimatePresence>
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-[#08080a] via-[#08080a]/70 to-transparent" />
        <span
          data-testid="gallery-caption"
          className="absolute bottom-4 left-0 right-0 px-5 text-center font-mono text-[10px] uppercase tracking-[0.16em] text-white/50"
        >
          {shot.caption}
        </span>
      </div>

      <div className="flex flex-wrap items-stretch border-t border-white/10 bg-white/[0.03]">
        <span className="hidden shrink-0 items-center border-r border-white/10 px-4 font-mono text-[9px] uppercase tracking-[0.18em] text-[hsl(var(--orv-blue))] lg:flex">
          {badge}
        </span>
        {FIGHT_STUDIO_MEDIA.map((m, i) => (
          <button
            key={m.src}
            onClick={() => setActive(i)}
            data-testid={`gallery-tab-${m.label.toLowerCase()}`}
            className={`flex-1 whitespace-nowrap px-2 py-3 font-mono text-[9px] uppercase tracking-[0.14em] transition-colors duration-300 ${
              active === i
                ? "bg-white/8 text-white"
                : "text-white/35 hover:bg-white/5 hover:text-white/70"
            }`}
          >
            {m.label}
          </button>
        ))}
      </div>
    </div>
  );
};

export const Store = () => {
  const { products } = useStore();
  const flagship = products.find((p) => p.slug === "fight-studio");
  const addon = products.find((p) => p.slug === "karate-gi");
  const fsCopy = PRODUCT_COPY["fight-studio"];
  const giCopy = PRODUCT_COPY["karate-gi"];

  return (
    <section id="store" className="relative border-b border-white/8 py-24 lg:py-36">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <div className="grid gap-10 pb-14 lg:grid-cols-12 lg:items-end">
          <div className="lg:col-span-7">
            <SectionLabel index="§ 01">The store</SectionLabel>
            <Reveal delay={0.05}>
              <h2 className="mt-6 font-display text-[clamp(2rem,4.6vw,3.6rem)] font-extrabold uppercase leading-[0.96] tracking-[-0.015em] text-white">
                Built by us.
                <br />
                <span className="text-white/35">Supported by us.</span>
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-5 lg:pb-3">
            <Reveal delay={0.12}>
              <p className="text-sm leading-relaxed text-white/45">
                Every Orvyn release is standalone, documented and supported in our Discord. Buy once,
                own it on every server you run, and get future updates through the same Tebex
                account.
              </p>
            </Reveal>
          </div>
        </div>

        <div className="grid gap-5 lg:grid-cols-12">
          {/* flagship */}
          {flagship && (
            <Reveal y={40} className="lg:col-span-8">
              <div
                data-testid="product-card-fight-studio"
                className="grain group relative flex h-full flex-col overflow-hidden border border-white/14 bg-[#0d0d11]"
              >
                <div className="edge-light absolute inset-x-0 top-0 z-10 h-px" />

                <Gallery badge={fsCopy.badge} />

                <div className="border-b border-white/10 p-8 sm:p-10">
                  <div className="flex flex-wrap items-start justify-between gap-6">
                    <div>
                      <h3 className="font-display text-3xl font-extrabold uppercase leading-none tracking-tight text-white sm:text-5xl">
                        Fight Studio
                      </h3>
                      <div className="mt-3 font-mono text-[11px] uppercase tracking-[0.2em] text-white/40">
                        {flagship.version} · {flagship.kind}
                      </div>
                      <p className="mt-4 max-w-md text-[13px] leading-relaxed text-[hsl(var(--orv-blue))]/80">
                        {fsCopy.tagline}
                      </p>
                    </div>
                    <PriceTag product={flagship} big />
                  </div>

                  <p className="mt-7 max-w-2xl text-sm leading-[1.85] text-white/50">
                    {fsCopy.blurb}
                  </p>

                  <div className="mt-9 flex flex-col gap-3 sm:flex-row">
                    <div className="sm:w-64">
                      <BuyButton product={flagship} big />
                    </div>
                    <a
                      href="#features"
                      data-testid="product-details-link"
                      className="group/link flex items-center justify-center gap-2.5 border border-white/15 px-7 py-4 font-mono text-[11px] uppercase tracking-[0.18em] text-white/70 transition-colors duration-300 hover:border-white/45 hover:text-white"
                    >
                      See everything it does
                      <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover/link:translate-x-1 group-hover/link:-translate-y-1" />
                    </a>
                  </div>
                </div>

                <div className="grid gap-x-8 gap-y-3.5 p-8 sm:grid-cols-2 sm:p-10">
                  {fsCopy.includes.map((f) => (
                    <div key={f} className="flex items-start gap-3 text-[13px] text-white/55">
                      <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[hsl(var(--orv-blue))]" />
                      {f}
                    </div>
                  ))}
                </div>
                <div className="mt-auto">
                  <PairsWith text={fsCopy.pairs} />
                </div>
              </div>
            </Reveal>
          )}

          {/* add-on */}
          {addon && (
            <Reveal y={40} delay={0.1} className="lg:col-span-4">
              <div
                data-testid="product-card-karate-gi"
                className="grain relative flex h-full flex-col overflow-hidden border border-white/12 bg-[#0b0b0e]"
              >
                <GiGallery badge={giCopy.badge} />

                <div className="border-b border-white/10 p-8">
                  <h3 className="font-display text-2xl font-extrabold uppercase leading-[1.05] tracking-tight text-white sm:text-[1.7rem]">
                    {addon.name}
                  </h3>
                  <div className="mt-3 font-mono text-[11px] uppercase tracking-[0.2em] text-white/40">
                    {addon.version} · {addon.kind}
                  </div>
                  <p className="mt-4 text-[13px] leading-relaxed text-[hsl(var(--orv-blue))]/80">
                    {giCopy.tagline}
                  </p>

                  <div className="mt-7">
                    <PriceTag product={addon} />
                  </div>

                  <p className="mt-6 text-[13px] leading-[1.8] text-white/45">{giCopy.blurb}</p>

                  <div className="mt-8">
                    <BuyButton product={addon} />
                  </div>
                  {!addon.available && (
                    <p className="mt-3 text-center font-mono text-[10px] tracking-[0.1em] text-white/25">
                      Releasing shortly
                    </p>
                  )}
                </div>

                <div className="space-y-3.5 p-8">
                  {giCopy.includes.map((f) => (
                    <div key={f} className="flex items-start gap-3 text-[13px] text-white/55">
                      <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[hsl(var(--orv-blue))]" />
                      {f}
                    </div>
                  ))}
                </div>
                <div className="mt-auto">
                  <PairsWith text={giCopy.pairs} />
                </div>
              </div>
            </Reveal>
          )}
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mt-8 text-center font-mono text-[10px] leading-relaxed tracking-[0.1em] text-white/25"
        >
          Secure Tebex checkout · You'll link your FiveM (Cfx.re) account so Tebex can deliver your
          files ·{" "}
          <a
            href={BRAND.discord}
            target="_blank"
            rel="noopener noreferrer"
            className="underline decoration-white/20 transition-colors hover:text-white/60"
          >
            Support in Discord
          </a>
        </motion.p>
      </div>
    </section>
  );
};
